import { LoanOfferResponse } from 'handlers/loanOffer';
import { EmployersLinkItemIds } from 'handlers/employmentData';
import { AICoachGoalType, AICoachGoalPriority, AICoachGoalDecisionType } from 'enums/AICoach';

import { CurrentFlow } from 'enums/CurrentFlow';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { ProfessionGroup } from 'enums/ProfessionGroup';
import { PartnerId } from 'enums/PartnerName';
import { Employer, GetApplicationDataResponse } from 'handlers/applicationData';

import Api, { ErrorMode, FetchMethodType } from './Api';

export interface AICoachGoalItem {
  id?: string;
  type: AICoachGoalType;
  description?: string;
  priority?: AICoachGoalPriority | null;
  decisionType?: AICoachGoalDecisionType;
  hasSavingsAccountId?: boolean;
  savingsAccountId?: string;
  targetValue?: number;
  monthlySavingsAmount?: number;
  isCompleted?: boolean;
}

export interface LoanOfferApplication {
  email: string;
  phoneNumber: string;
  argyleState?: string;
  loanAmountRange: string;
  loanPurpose: string;
  creditScoreRange: string;
  totalAnnualIncome: number;
  additionalIncome?: number;
  isFullAddressSent: boolean;
  loanGoal?: string;
  referredBy: string;
  resumeLink: string;
}

export interface UtmTags {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
}

interface LoanOfferBorrower {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  zipCode: string;
  email: string;
  city: string;
  state: string;
  dateOfBirth: string;
  credentials?: string;
  streetAddress: string;
  personalIdNumber: string;
}

export interface DataForGettingLoanOffer {
  application: LoanOfferApplication;
  borrower: LoanOfferBorrower;
  employment: Employer[];
  timezone: string;
  utm: UtmTags;
  employersLinkItemIds?: EmployersLinkItemIds;
  sessionToken?: string;
  aiCoachGoals?: AICoachGoalItem[];
}

export interface DataForGettingLoanOfferInFinancialCheckup {
  application: {
    credentials?: string;
    credentialsAcquisitionDate?: string;
    professionGroup: ProfessionGroup;
    entityId?: string;
    totalAnnualIncome: number;
    resumeLink: string;
    partnerId?: PartnerId;
  };
  borrower: Pick<LoanOfferBorrower, 'firstName' | 'lastName' | 'phoneNumber' | 'email'>;
  notEmployed: boolean;
  employment: Employer[];
  timezone: string;
  currentFlow: CurrentFlow;
  sessionToken?: string;
}

export interface DataForGettingLoanOfferXSellFromCardApplication {
  applicationId: string;
  flow: CurrentFlow;
  resumeLink: string;
}

export interface LoanOfferProgress {
  step: LoanOfferStep;
  applicationId?: string;
  totalAccounts?: number;
  initialAccounts?: number;
  syncingAccounts?: number;
}

export enum LoanOfferStep {
  ValidatedInput = 'Validated Input',
  RunningDecisionEngine = 'Running Decision Engine',
  GettingCreditReport = 'Getting Credit Report',
  GettingTradelines = 'Getting Tradelines',
  SyncingTradelines = 'Syncing Tradelines',
  GeneratingDebtProfile = 'Generating Debt Profile',
  CreatingHardOffer = 'Creating Hard Offer',
}

export interface DataForVerifyingApplication {
  applicationId: string;
  kycVariables: {
    iovationBlackbox: string;
  };
}

export interface VerificationResult {
  status: VerifyStatus;
  applicationStatus: ApplicationStatusName;
  kycPending?: boolean;
  underwritingPricingDifference?: boolean;
}

export interface PreVerifyResult {
  needsVerifyIncomeOption?: boolean;
  paystubsRequired?: boolean;
}

export enum VerifyStatus {
  PendingDocuments = 'Pending Documents',
  PendingPaysubs = 'Pending Paysubs',
  Verified = 'Verified',
  ManualVerify = 'Manual Verify',
  Rejected = 'Rejected',
}

export interface LoanOfferApi {
  getLoanOffer(
    dataForGettingLoanOffer: DataForGettingLoanOffer | DataForGettingLoanOfferInFinancialCheckup,
    updateCallback?: (data: LoanOfferProgress) => void,
  ): Promise<LoanOfferResponse>;
  updateLoanOffer(
    applicationId: string,
    updateCallback?: (data: LoanOfferProgress) => void,
  ): Promise<LoanOfferResponse>;
  updateOfferWithVerifiedIncome(applicationId: string): Promise<GetApplicationDataResponse>;
  getLoanOfferXSell(
    dataForGettingLoanOffer: DataForGettingLoanOfferXSellFromCardApplication,
    updateCallback?: (data: LoanOfferProgress) => void,
  ): Promise<LoanOfferResponse>;
  preVerifyApplication(applicationId: string): Promise<PreVerifyResult>;
  verifyApplication(data: DataForVerifyingApplication): Promise<VerificationResult>;
}

export default class LoanOfferRestApi extends Api implements LoanOfferApi {
  public async getLoanOffer(
    dataForGettingLoanOffer: DataForGettingLoanOffer | DataForGettingLoanOfferInFinancialCheckup,
    updateCallback?: (data: LoanOfferProgress) => void,
  ) {
    const result = this.fetchStream<LoanOfferProgress | LoanOfferResponse>(`/loanOffer`, {
      method: FetchMethodType.POST,
      body: dataForGettingLoanOffer,
      redirectToLogin: false,
    });

    let next;
    // eslint-disable-next-line no-restricted-syntax
    for await (next of result) {
      if (LoanOfferRestApi.isOfferProgress(next)) {
        updateCallback && updateCallback(next as LoanOfferProgress);
      } else {
        break;
      }
    }
    return next as LoanOfferResponse;
  }

  public async updateLoanOffer(applicationId: string, updateCallback?: (data: LoanOfferProgress) => void) {
    const result = this.fetchStream<LoanOfferProgress | LoanOfferResponse>(`/loanOffer/${applicationId}`, {
      method: FetchMethodType.PUT,
    });

    let next;
    // eslint-disable-next-line no-restricted-syntax
    for await (next of result) {
      if (LoanOfferRestApi.isOfferProgress(next)) {
        updateCallback && updateCallback(next as LoanOfferProgress);
      } else {
        break;
      }
    }
    return next as LoanOfferResponse;
  }

  public async updateOfferWithVerifiedIncome(applicationId: string): Promise<GetApplicationDataResponse> {
    return this.fetch<GetApplicationDataResponse>(`/loanOffer/updateOfferWithVerifiedIncome/${applicationId}`, {
      method: FetchMethodType.PUT,
    });
  }

  public async getLoanOfferXSell(
    dataForGettingLoanOffer: DataForGettingLoanOfferXSellFromCardApplication,
    updateCallback?: (data: LoanOfferProgress) => void,
  ) {
    const result = this.fetchStream<LoanOfferProgress | LoanOfferResponse>(`/loanOffer/xsell`, {
      method: FetchMethodType.POST,
      body: {
        ...dataForGettingLoanOffer,
      },
      errorMode: ErrorMode.HideError,
    });

    let next;
    // eslint-disable-next-line no-restricted-syntax
    for await (next of result) {
      if (LoanOfferRestApi.isOfferProgress(next)) {
        updateCallback && updateCallback(next as LoanOfferProgress);
      } else {
        break;
      }
    }
    return next as LoanOfferResponse;
  }

  public async preVerifyApplication(applicationId: string): Promise<PreVerifyResult> {
    return this.fetch<PreVerifyResult>(`/loanOffer/preVerify/${applicationId}`, {
      method: FetchMethodType.GET,
    }) as Promise<PreVerifyResult>;
  }

  public async verifyApplication(data: DataForVerifyingApplication): Promise<VerificationResult> {
    const { applicationId, ...body } = data;
    return this.fetch<VerificationResult>(`/loanOffer/verify/${applicationId}`, {
      method: FetchMethodType.POST,
      body,
    }) as Promise<VerificationResult>;
  }

  private static isOfferProgress(result: LoanOfferProgress | LoanOfferResponse): result is LoanOfferProgress {
    return !!(result as LoanOfferProgress).step;
  }
}
